import React, { useEffect, useState } from 'react';
import SubHeader from '../layout/components/SubHeader';
import { useEyeconxStore } from '../../store/eyeconxStore';
import {
    fetchWhatsNew,
    formatWhatsNewDate,
    getLatestWhatsNewVersion,
    markWhatsNewSeen,
    normalizeReleases,
} from '../../config/whatsNew';
import './WhatsNewPage.css';

function WhatsNewPage() {
    const clinicType = useEyeconxStore((state) => state.user?.clinicType);
    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isSmsGateway = clinicType === 'SMS_GATEWAY';
    const pageDescription = isSmsGateway
        ? 'Release notes and new features for SMS Gateway'
        : 'Release notes and new features across EyeconX';

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchWhatsNew();
                if (cancelled) {
                    return;
                }
                const normalized = normalizeReleases(data, clinicType);
                setReleases(normalized);
                const latest = getLatestWhatsNewVersion(data, clinicType);
                if (latest) {
                    markWhatsNewSeen(latest);
                }
            } catch (e) {
                if (!cancelled) {
                    setError(e.message || "Couldn't load release notes.");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [clinicType]);

    return (
        <div className="ecx-page whats-new-page">
            <SubHeader
                title="What's New"
                description={pageDescription}
                icon="bi-stars"
                iconClass="text-primary"
                containerClass="container-fluid px-4"
            />

            <div className="container-fluid px-4 py-3">
                {loading && (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body text-center py-5 text-muted">
                            <div className="spinner-border spinner-border-sm text-primary mb-3" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mb-0">Loading release notes...</p>
                        </div>
                    </div>
                )}

                {!loading && error && (
                    <div className="alert alert-warning mb-0">
                        <i className="bi bi-exclamation-triangle me-2" aria-hidden="true" />
                        {error}
                    </div>
                )}

                {!loading && !error && releases.length === 0 && (
                    <div className="card border-0 shadow-sm">
                        <div className="card-body text-center py-5">
                            <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
                                <i className="bi bi-stars text-primary" style={{ fontSize: '2rem' }} />
                            </div>
                            <h5 className="fw-normal mb-2">No release notes yet</h5>
                            <p className="text-muted mb-0">Check back after the next product update.</p>
                        </div>
                    </div>
                )}

                {!loading && !error && releases.map((release, index) => (
                    <article
                        key={release.version}
                        className={`card border-0 shadow-sm whats-new-release ${index === 0 ? 'whats-new-release--latest' : ''}`}
                    >
                        <div className="card-body p-4">
                            <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-3">
                                <div>
                                    <div className="d-flex align-items-center gap-2 mb-1">
                                        {index === 0 && (
                                            <span className="badge bg-primary bg-opacity-10 text-primary">
                                                Latest
                                            </span>
                                        )}
                                        <span className="badge bg-light text-muted border">
                                            {release.version}
                                        </span>
                                    </div>
                                    <h2 className="h5 fw-normal mb-1">{release.title || "What's New"}</h2>
                                    {release.description && (
                                        <p className="text-muted mb-0">{release.description}</p>
                                    )}
                                </div>
                                {release.date && (
                                    <small className="text-muted">
                                        <i className="bi bi-calendar3 me-1" aria-hidden="true" />
                                        {formatWhatsNewDate(release.date)}
                                    </small>
                                )}
                            </div>

                            <ul className="list-unstyled mb-0 whats-new-release__features">
                                {(release.features || []).map((feature) => (
                                    <li key={`${release.version}-${feature.title}`} className="whats-new-release__feature">
                                        <div className="whats-new-release__icon" aria-hidden="true">
                                            <i className={`bi ${feature.icon || 'bi-sparkles'}`} />
                                        </div>
                                        <div>
                                            <h3 className="whats-new-release__feature-title">{feature.title}</h3>
                                            <p className="whats-new-release__feature-desc mb-0">
                                                {feature.description}
                                            </p>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
}

export default WhatsNewPage;
